import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button,Skeleton,Stack } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import MUIDataTable,{ MUIDataTableOptions } from 'mui-datatables';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiDeleteBin5Line,RiPencilFill } from 'react-icons/ri';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import QueryFn from '../../helpers/QueryFn';
import checklistStyle from '../../service/checklistStyle';
import type { modeloStyleType } from '../../types/print-template/types';
import queryClient from '../../service/query';
import { getMuiTheme } from "../TableHelpers/options";
import { tableOptions } from '../TableHelpers/TableOptions';
import { StyledDiv } from '../TableHelpers/TableStyle';
import EditFormDeslogadoTabs from './Modals/StyleFill/EditFormDeslogadoTabs';

const modelService = new checklistStyle();
const queryFn = new QueryFn();

export const initialObjectToSend: modeloStyleType = {
  name: 'Nome do estilo',
  fontHeader: {
    fontFamily: 'Arial',
    fontSize: 30,
    color: '#000',
  },
  fontBackground: {
    fontFamily: 'Arial',
    fontSize: 20,
    color: '#000',
  },
  logo: null,
  associatedChecklists: [],
  enablePosMessage: false,
  enablePostMessage: false,
  enableLoop: false,
  enableRedirect: false,
  messageBefore: null,
  imageBefore: null,
  messageAfter: null,
  imageAfter: null,
  link: null
};

export default function ChecklistStyle() {
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState<boolean>(false);
  const { t } = useTranslation('translation');
  const [objectToEdit, setObjectToEdit] = useState<modeloStyleType | null>(initialObjectToSend);


  const result = queryFn.useFetch('GET_MODELS', async () => {
    try {
      const token = await AttToken();
      if (token) {
        const response = await modelService.getAll(token);
        return response;
      }
    } catch (err) {
      console.error(err);
    }
  });

  const mutate = queryFn.useMutate('DELETE_MODEL', async (value?: string) => {
    try {
      toast.warn(`${t('toast.deletingTemplate')}`);
      const token = await AttToken();
      if (token) {
        await modelService.delete(token, value!);
        queryClient.resetQueries({ queryKey: ['GET_MODELS'] });
        toast.success(`${t('toast.templateDeleted')}`);
      }
    } catch (err) {
      console.error(err);
    }
  });

  const columns = [
    {
      name: "name",
      label: t('name'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('edit'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              endIcon={<RiPencilFill />}
              className='button'
              onClick={() => {
                setEditing(true);
                setObjectToEdit(result.data?.modelos?.find((elem: modeloStyleType) => elem.id === value));
                trackEventMatomo('Configurar impressões', 'click', 'button', 'editar');
                setOpenModal(true);
              }}>
              {t('edit')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
    {
      name: "id",
      label: t('delete'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              endIcon={<RiDeleteBin5Line />}
              className='button is-danger'
              onClick={() => {
                trackEventMatomo('Configurar impressões', 'click', 'button', 'deletar');
                mutate.mutateAsync(value);
              }}>
              {t('delete')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      }
    }
  };

  return (
    <>
      {openModal && (
        <EditFormDeslogadoTabs
          editing={editing}
          setEditing={setEditing}
          openModal={openModal}
          setOpenModal={setOpenModal}
          setObjectToEdit={setObjectToEdit}
          objectToEdit={objectToEdit as modeloStyleType}
        />
      )}
      <div style={{ width: '100%', textAlign: 'center', fontSize: '1.2em' }}>
        {t('checklists.webExecutionChecklistStyling')}
      </div>
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        width={'100%'}
      >
        <Button
          endIcon={<AddCircleIcon />}
          className="button is-primary"
          onClick={() => {
            trackEventMatomo('Configurar impressões', 'click', 'button', 'cria novo modelo');
            setOpenModal(true);
          }}
        >
          {t('checklists.newTemplate')}
        </Button>
        <Stack
          justifyContent="flex-start"
          alignItems="stretch"
          width={'100%'}
        >
          <StyledDiv>
            {result.isLoading ? <Skeleton variant='rounded' height={400} /> :
              <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  title={`${t('checklists.templateModels')}`}
                  columns={columns}
                  data={result.data.modelos}
                  options={{ ...tableOptions, ...translatedTextLabels }}
                />
              </ThemeProvider>
            }
          </StyledDiv>
        </Stack>
      </Stack>
    </>
  );
}
